/**
 * Contrato de MARKUP EXPRESIVO de la voz (speak sobre Gemini TTS, 2026-09-24).
 *
 * El LLM del coach puede anotar su reply con direcciones vocales que el TTS
 * de Gemini ACTÚA (no lee): eventos (`<chuckle>`, `<short pause>`…) y un
 * ánimo por oración (`<mood:excited>` al inicio). El prompt sólo enseña el
 * markup cuando el TTS activo lo soporta ({@link ttsProviderSupportsSpeechMarkup}).
 *
 * Punto ÚNICO de separación: la salida del LLM se parte en DOS streams —
 *   - DISPLAY: sin markup, whitespace normalizado. Es lo que el user lee y lo
 *     que se persiste/analiza (burbuja, `turns.assistant_text`, historial del
 *     LLM, distill, correcciones, traducción).
 *   - SPEECH: markup whitelisteado conservado (forma canónica). Sólo lo
 *     consume el TTS que lo sabe actuar.
 * Un `<...>` con FORMA de tag (`<` + letra + [letras : _ - espacio], 2-21
 * chars, `>`; un espacio tras el `<` sólo si el nombre está en la whitelist:
 * `< mood:playful>`) que no está en la whitelist — o cualquier tag de CIERRE `</...>`
 * con esa forma — se descarta de AMBOS streams — nunca
 * se lee en voz alta ni se muestra. Tope: un solo evento vocal por reply
 * ({@link MAX_SPEECH_EVENTS_PER_REPLY}; los siguientes se descartan), ninguna
 * pausa antes de la primera palabra del reply, y alias cerrados de ánimo (el nombre
 * pelado `<gentle>` y `<softly>` → `<mood:gentle>`). Cualquier otro `<` es texto literal
 * ("3 < 5", "<3"). Los tags de extractors aguas abajo del display
 * (`<correction …/>`, `<pronunciation_correction …/>`; opción
 * `displayOnlyTags`) van enteros SÓLO al display: el stream hablado no pasa
 * por esos extractors, así que jamás deben entrar en él.
 *
 * Invariante fuerte: un texto SIN tags sale byte-idéntico en ambos streams
 * (ElevenLabs, karaoke y los evals no ven ningún cambio). El énfasis
 * (`*palabra*`) NO es asunto de este módulo: queda en ambos streams y cada
 * consumidor decide (burbuja: render de markdown; Gemini: MAYÚSCULAS;
 * ElevenLabs: `stripMarkdownForSpeech`).
 *
 * Streaming-safe: un tag partido entre deltas (`<chu` + `ckle>`) se retiene
 * hasta resolverse, con lookahead acotado ({@link MAX_SPEECH_TAG_LOOKAHEAD}).
 * Del display además se retiene el whitespace final de cada delta (se decide
 * con el char siguiente si sobrevive a un tag removido) — mismo costo cero que
 * el suavizado de rayas.
 */
import type { TtsProviderId } from './gemini-tts-voices.js';

/**
 * Eventos vocales que el TTS actúa. Whitelist CERRADA: cada uno se verificó
 * sintetizándolo con Gemini y transcribiendo con ElevenLabs Scribe — se ACTÚA,
 * la palabra del tag nunca aparece como texto hablado.
 *
 * Curaduría para un profesor de inglés (Gemini 3.8 soporta ~30 eventos; se
 * suman sólo los que tienen un uso pedagógico claro, 2026-09-24):
 *   - `cheer`   → festejar un logro REAL del alumno (no cada respuesta).
 *   - `giggle`  → diversión liviana en juegos.
 *   - `exhales` → alivio / "phew" después de un ejercicio difícil.
 *   - `tsk`     → "uy, casi" juguetón en un juego (casi acierto, tiempo que se
 *                 acaba) — NUNCA ante el error del alumno.
 * Descartados a propósito: sob/cry/whimper (tristeza teatral), scream/shout/
 * shriek (agresivo), moan/groan/growl/grunt/grr/argh/hiss (fastidio),
 * cackle/snicker (se leen como burla), yawn (aburrimiento), pff (desdén),
 * cough/sneeze/snort/throat-clearing/pant/heavy breath (ruido sin intención).
 *
 * Qué eventos enseña el prompt y deja pasar el splitter en speak se regula
 * sin deploy con `module_config.languages.speechMarkup` ({@link SpeechMarkupPolicy});
 * esta lista es el techo.
 */
export const SPEECH_EVENTS = [
  'chuckle',
  'laugh',
  'sigh',
  'gasp',
  'breath',
  'short pause',
  'long pause',
  'cheer',
  'giggle',
  'exhales',
  'tsk',
] as const;
export type SpeechEvent = (typeof SPEECH_EVENTS)[number];

/**
 * Dial del markup expresivo (en speak: `module_config.languages.speechMarkup`).
 *  - `enabled=false` → el prompt no enseña markup y el stream hablado es el
 *    display (se comporta como ElevenLabs).
 *  - `events` → subconjunto de {@link SPEECH_EVENTS} que el prompt lista y el
 *    splitter deja pasar (cualquier otro evento se descarta de ambos streams).
 *    Ánimos y énfasis dependen sólo de `enabled`.
 */
export interface SpeechMarkupPolicy {
  readonly enabled: boolean;
  readonly events: readonly SpeechEvent[];
}

/**
 * Valida una lista de eventos contra la whitelist: tira ante un nombre
 * desconocido o repetido (fail-fast de config, sin descartar en silencio).
 */
export function parseSpeechEvents(names: readonly string[]): SpeechEvent[] {
  const seen = new Set<string>();
  for (const n of names) {
    if (!(SPEECH_EVENTS as readonly string[]).includes(n)) {
      throw new Error(
        `speech markup: evento desconocido "${n}" (whitelist: ${SPEECH_EVENTS.join(', ')})`,
      );
    }
    if (seen.has(n)) throw new Error(`speech markup: evento duplicado "${n}"`);
    seen.add(n);
  }
  return [...names] as SpeechEvent[];
}

/** Ánimos por oración (`<mood:x>` al inicio). `warm` = default sin tag. */
export const SPEECH_MOODS = [
  'warm',
  'excited',
  'gentle',
  'playful',
  'whisper',
  'thoughtful',
  'slow',
] as const;
export type SpeechMood = (typeof SPEECH_MOODS)[number];

/** Eventos que son silencio puro (el cliente de Gemini los puede materializar como PCM). */
export const SPEECH_PAUSE_EVENTS: readonly SpeechEvent[] = ['short pause', 'long pause'];

/** Máximo que se retiene esperando que un `<` se resuelva como tag o texto. */
export const MAX_SPEECH_TAG_LOOKAHEAD = 24;

/** Largo máximo del interior de un tag (`<` + interior + `>` ≤ 23 ≤ lookahead). */
const MAX_INNER = 21;
const INNER_FIRST = /[A-Za-z]/;
const INNER_REST = /[A-Za-z:_ -]/;
const INNER_RE = /^[A-Za-z][A-Za-z:_ -]{1,20}$/;
/**
 * Espacio entre `<` y el nombre (`< mood:playful>`, caso real card 599: el
 * primer turno de una lección mostró la marca). Se tolera UNO, sólo en un tag
 * de apertura y sólo si el nombre está en la whitelist: "x < y and z>" sigue
 * siendo prosa. Con uno el candidato más largo sigue entrando en
 * {@link MAX_SPEECH_TAG_LOOKAHEAD} (`<` + espacio + 21 + `>`).
 */
const MAX_LEAD_SPACES = 1;
/** Letra o número: marca que el reply ya empezó a decir algo. */
const WORD_CHAR = /[\p{L}\p{N}]/u;
/** Puntuación que no debe quedar precedida por el espacio de un tag removido. */
const CLOSING_PUNCT = /[.,!?;:)\]…]/;

const EVENT_SET: ReadonlySet<string> = new Set(SPEECH_EVENTS);
const MOOD_SET: ReadonlySet<string> = new Set(SPEECH_MOODS);

/**
 * Tope de eventos vocales por reply en el stream hablado: el prompt pide "nunca
 * más de un sonido por turno" y el modelo a veces mete dos (medido 2026-09-24:
 * máx 2/turno en reply-bench). Se conserva el PRIMERO; el resto se descarta de
 * ambos streams. Los ánimos no cuentan (uno por oración es el contrato).
 */
export const MAX_SPEECH_EVENTS_PER_REPLY = 1;

/**
 * Alias CERRADOS de ánimo: tags que el modelo inventa con una intención
 * inequívoca dentro de la whitelist. Caso real (reply-bench 2026-09-24): ante
 * un duelo escribió `<softly>` dos veces en vez de `<mood:gentle>`. Mapearlo
 * rescata la intención sin abrir la whitelist (sigue siendo una lista fija).
 */
const MOOD_ALIASES: Readonly<Record<string, SpeechMood>> = {
  softly: 'gentle',
};

/** Formas canónicas de todos los tags válidos (para detectar truncados al final). */
const CANONICAL_TAGS: readonly string[] = [
  ...SPEECH_EVENTS.map((e) => `<${e}>`),
  ...SPEECH_MOODS.map((m) => `<mood:${m}>`),
  ...SPEECH_MOODS.map((m) => `<${m}>`),
  ...Object.keys(MOOD_ALIASES).map((a) => `<${a}>`),
];

/** ¿El TTS de este provider ACTÚA el markup? Hoy sólo Gemini. */
export function ttsProviderSupportsSpeechMarkup(provider: TtsProviderId): boolean {
  return provider === 'gemini';
}

export type SpeechTagClass =
  | { kind: 'event'; event: SpeechEvent }
  | { kind: 'mood'; mood: SpeechMood }
  | { kind: 'unknown' };

function normalizeInner(inner: string): string {
  return inner
    .toLowerCase()
    .replace(/[_-]/g, ' ')
    .replace(/\s*:\s*/g, ':')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Clasifica el INTERIOR de un tag (sin `<>`), tolerando mayúsculas y `_`/`-`. */
export function classifySpeechTag(inner: string): SpeechTagClass {
  const n = normalizeInner(inner);
  if (EVENT_SET.has(n)) return { kind: 'event', event: n as SpeechEvent };
  // Ánimo sin el prefijo `mood:` (`<gentle>`, caso real r2): mismo nombre de la whitelist.
  if (MOOD_SET.has(n)) return { kind: 'mood', mood: n as SpeechMood };
  const alias = MOOD_ALIASES[n];
  if (alias) return { kind: 'mood', mood: alias };
  if (n.startsWith('mood:')) {
    const mood = n.slice('mood:'.length);
    if (MOOD_SET.has(mood)) return { kind: 'mood', mood: mood as SpeechMood };
  }
  return { kind: 'unknown' };
}

function canonicalTag(c: SpeechTagClass): string | null {
  if (c.kind === 'event') return `<${c.event}>`;
  if (c.kind === 'mood') return `<mood:${c.mood}>`;
  return null;
}

export interface SpeechMarkupChunk {
  /** Texto para leer/persistir: sin markup. */
  readonly display: string;
  /** Texto para el TTS expresivo: markup whitelisteado en forma canónica. */
  readonly speech: string;
  /** Tags con forma válida pero fuera de la whitelist (descartados de ambos). */
  readonly dropped: readonly string[];
}

/** Tope de un tag display-only sin cerrar: pasado esto, el resto vuelve al flujo normal. */
export const MAX_DISPLAY_ONLY_TAG_CHARS = 1500;

export interface SpeechMarkupSplitterOptions {
  /**
   * Nombres de tags que son de OTRO consumidor aguas abajo del display (hoy
   * `correction` y `pronunciation_correction`, de sus extractors): van ENTEROS
   * al display (el extractor los saca de ahí) y NUNCA al speech. Hace falta
   * porque el stream hablado no pasa por esos extractors — sin esto el TTS
   * leía el tag (review 2026-09-24: un `.` dentro de `tip="…"` partía la
   * oración y el cliente de Gemini ya no podía reconocer el tag).
   * Formas: self-closing `<name …/>` (cierra en `/>`) y contenedor
   * `<name>…</name>`. Acotado por {@link MAX_DISPLAY_ONLY_TAG_CHARS}.
   */
  readonly displayOnlyTags?: readonly string[];
  /**
   * Subconjunto de eventos permitidos (dial `speechMarkup.events`). Ausente =
   * toda la whitelist. Un evento fuera del subconjunto se descarta de ambos
   * streams como un tag desconocido (no consume el tope de un sonido). Un
   * nombre fuera de la whitelist tira (fail-fast).
   */
  readonly allowedEvents?: readonly SpeechEvent[];
}

export interface SpeechMarkupSplitter {
  push(delta: string): SpeechMarkupChunk;
  /** Fin del stream: suelta lo retenido. */
  flush(): SpeechMarkupChunk;
}

/**
 * Crea el splitter para UN reply (instanciar de nuevo por reply: el estado
 * retenido no debe cruzar turnos).
 */
export function createSpeechMarkupSplitter(
  opts: SpeechMarkupSplitterOptions = {},
): SpeechMarkupSplitter {
  const displayOnlyTags = opts.displayOnlyTags ?? [];
  const allowedEvents: ReadonlySet<string> = opts.allowedEvents
    ? new Set(parseSpeechEvents(opts.allowedEvents))
    : EVENT_SET;
  /** Tag display-only en curso: su marcador de cierre y cuánto lleva. */
  let displayOnly: { close: string; chars: number } | null = null;
  let buf = '';
  let heldWs = '';
  let afterTag = false;
  let emittedAny = false;
  let lastChar = '';
  let display = '';
  let speech = '';
  let dropped: string[] = [];
  /** Eventos vocales ya emitidos en ESTE reply (tope {@link MAX_SPEECH_EVENTS_PER_REPLY}). */
  let events = 0;
  /** ¿Ya salió alguna palabra (letra/número)? Antes de eso, una pausa se descarta. */
  let spokeWord = false;

  const literal = (text: string): void => {
    speech += text;
    for (const c of text) {
      if (c === ' ' || c === '\t') {
        heldWs += c;
        continue;
      }
      if (afterTag) {
        const dropWs =
          heldWs.length === 0 || !emittedAny || CLOSING_PUNCT.test(c) || lastChar === '\n';
        if (!dropWs) display += ' ';
        heldWs = '';
        // Tag al inicio seguido de salto de línea: sin primera línea en blanco.
        if (!emittedAny && c === '\n') continue;
      } else if (heldWs.length > 0) {
        display += heldWs;
        heldWs = '';
      }
      display += c;
      if (!spokeWord && WORD_CHAR.test(c)) spokeWord = true;
      lastChar = c;
      emittedAny = true;
      afterTag = false;
    }
  };

  const tag = (raw: string, inner: string): void => {
    const parsed = classifySpeechTag(inner);
    // Evento fuera del subconjunto configurado → como un tag desconocido.
    const cls: SpeechTagClass =
      parsed.kind === 'event' && !allowedEvents.has(parsed.event) ? { kind: 'unknown' } : parsed;
    // PAUSA antes de la primera palabra (`<short pause> Yesterday you…?`): no
    // aporta nada (el cliente de Gemini ni la materializa al abrir el turno) y
    // consumiría el único sonido del reply. No consume el tope. Una risa o un
    // suspiro al abrir SÍ se conservan: es una reacción legítima; su frecuencia
    // la regula el prompt (dropearlos bajaba el uso efectivo a ~4%, medido r2).
    const leading = cls.kind === 'event' && !spokeWord && SPEECH_PAUSE_EVENTS.includes(cls.event);
    const overCap = cls.kind === 'event' && events >= MAX_SPEECH_EVENTS_PER_REPLY;
    const canonical = leading || overCap ? null : canonicalTag(cls);
    if (canonical) {
      speech += canonical;
      if (cls.kind === 'event') events++;
    } else {
      dropped.push(raw);
    }
    afterTag = true;
  };

  /** Texto que va SÓLO al display, verbatim (lo consume un extractor aguas abajo). */
  const displayVerbatim = (text: string): void => {
    if (heldWs.length > 0) {
      display += heldWs;
      heldWs = '';
    }
    display += text;
    afterTag = false;
  };

  /** `buf` arranca con `<`: ¿abre un tag display-only? */
  const matchDisplayOnly = (): 'no' | 'pending' | { close: string } => {
    let pending = false;
    for (const name of displayOnlyTags) {
      const open = `<${name}`;
      if (buf.length <= open.length) {
        if (open.startsWith(buf)) pending = true;
        continue;
      }
      if (!buf.startsWith(open)) continue;
      const next = buf[open.length] as string;
      if (next === '>') return { close: `</${name}>` };
      if (next === '/' || /\s/.test(next)) return { close: '/>' };
    }
    return pending ? 'pending' : 'no';
  };

  /** Consume `buf` dentro de un tag display-only. `true` = salir de drain (esperar más). */
  const drainDisplayOnly = (final: boolean): boolean => {
    const mode = displayOnly as { close: string; chars: number };
    const end = buf.indexOf(mode.close);
    if (end >= 0) {
      displayVerbatim(buf.slice(0, end + mode.close.length));
      buf = buf.slice(end + mode.close.length);
      displayOnly = null;
      return false;
    }
    if (final) {
      displayVerbatim(buf);
      buf = '';
      displayOnly = null;
      return true;
    }
    // Retener sólo lo que podría ser el comienzo del marcador de cierre.
    const keep = Math.min(buf.length, mode.close.length - 1);
    const out = buf.slice(0, buf.length - keep);
    displayVerbatim(out);
    buf = buf.slice(out.length);
    mode.chars += out.length;
    if (mode.chars > MAX_DISPLAY_ONLY_TAG_CHARS) displayOnly = null;
    return true;
  };

  const drain = (final: boolean): void => {
    while (buf.length > 0) {
      if (displayOnly) {
        if (drainDisplayOnly(final)) return;
        continue;
      }
      const lt = buf.indexOf('<');
      if (lt === -1) {
        literal(buf);
        buf = '';
        return;
      }
      if (lt > 0) {
        literal(buf.slice(0, lt));
        buf = buf.slice(lt);
      }
      // `buf` arranca con `<`: ¿tag de un extractor? (antes que el scan normal:
      // `<correction said=…` no tiene forma de tag de voz y saldría literal).
      if (displayOnlyTags.length > 0) {
        const m = matchDisplayOnly();
        if (m === 'pending' && !final) return;
        if (typeof m === 'object') {
          displayOnly = { close: m.close, chars: 0 };
          continue;
        }
      }
      // `buf` arranca con `<`: escanear el candidato.
      // `</x>`: tag de CIERRE (el modelo a veces inventa envoltorios tipo
      // `<loud>Ship</loud>`, caso real 2026-09-24) — misma forma, siempre se descarta.
      const closing = buf[1] === '/';
      const start = closing ? 2 : 1;
      let j = start;
      if (!closing) while (j < buf.length && buf[j] === ' ' && j - start < MAX_LEAD_SPACES) j++;
      const innerStart = j;
      const spaced = innerStart > start;
      let resolved: 'tag' | 'literal' | 'pending' = 'pending';
      while (j < buf.length) {
        const c = buf[j] as string;
        if (c === '>') {
          const inner = buf.slice(innerStart, j);
          const known = !spaced || classifySpeechTag(inner).kind !== 'unknown';
          resolved = INNER_RE.test(inner) && known ? 'tag' : 'literal';
          break;
        }
        const ok = j === innerStart ? INNER_FIRST.test(c) : INNER_REST.test(c);
        if (!ok || j - innerStart >= MAX_INNER) {
          resolved = 'literal';
          break;
        }
        j++;
      }
      if (resolved === 'tag') {
        const raw = buf.slice(0, j + 1);
        if (closing) {
          dropped.push(raw);
          afterTag = true;
        } else {
          tag(raw, buf.slice(innerStart, j));
        }
        buf = buf.slice(j + 1);
        continue;
      }
      if (resolved === 'literal') {
        literal('<');
        buf = buf.slice(1);
        continue;
      }
      // Candidato incompleto.
      if (!final) return;
      // Con espacio tras el `<` («a < b» al final) es prosa: un tag a medias
      // sólo se reconoce pegado al `<`.
      const partial = buf[1] === ' ' ? '' : normalizeInner(buf.slice(1));
      const truncatedTag =
        partial.length > 0 && CANONICAL_TAGS.some((t) => t.startsWith(`<${partial}`));
      if (truncatedTag) {
        dropped.push(buf);
        afterTag = true;
        buf = '';
        return;
      }
      literal('<');
      buf = buf.slice(1);
    }
  };

  const take = (): SpeechMarkupChunk => {
    const out = { display, speech, dropped };
    display = '';
    speech = '';
    dropped = [];
    return out;
  };

  return {
    push(delta: string) {
      buf += delta;
      drain(false);
      return take();
    },
    flush() {
      drain(true);
      if (heldWs.length > 0 && !afterTag) display += heldWs;
      heldWs = '';
      return take();
    },
  };
}

/** Split de un texto COMPLETO (mismo resultado que streamearlo en cualquier troceo). */
export function splitSpeechMarkup(
  text: string,
  opts: SpeechMarkupSplitterOptions = {},
): SpeechMarkupChunk {
  const s = createSpeechMarkupSplitter(opts);
  const a = s.push(text);
  const b = s.flush();
  return {
    display: a.display + b.display,
    speech: a.speech + b.speech,
    dropped: [...a.dropped, ...b.dropped],
  };
}

/** Texto para mostrar/persistir/juzgar: sin markup de voz. */
export function stripSpeechMarkup(text: string): string {
  return splitSpeechMarkup(text).display;
}

export interface SpeechMarkupAudit {
  readonly events: Partial<Record<SpeechEvent, number>>;
  readonly moods: Partial<Record<SpeechMood, number>>;
  readonly totalEvents: number;
  /** Spans de énfasis `*palabra*` (asterisco simple, no `**negrita**`). */
  readonly emphasis: number;
  readonly unknownTags: readonly string[];
}

const EMPHASIS_RE = /(?<!\*)\*(?!\*)([^*\n]{1,40}?)\*(?!\*)/g;
const TAG_SCAN_RE = /<( ?)(\/?[A-Za-z][A-Za-z:_ -]{1,20})>/g;

/** Auditoría del texto CRUDO del LLM (para evals): qué markup usó y cuánto. */
export function auditSpeechMarkup(raw: string): SpeechMarkupAudit {
  const events: Partial<Record<SpeechEvent, number>> = {};
  const moods: Partial<Record<SpeechMood, number>> = {};
  const unknownTags: string[] = [];
  let totalEvents = 0;
  for (const m of raw.matchAll(TAG_SCAN_RE)) {
    const spaced = m[1] === ' ';
    const inner = m[2] as string;
    const cls: SpeechTagClass = inner.startsWith('/')
      ? { kind: 'unknown' }
      : classifySpeechTag(inner);
    // Con espacio tras el `<`, el splitter sólo lo toma si es markup conocido.
    if (spaced && cls.kind === 'unknown') continue;
    if (cls.kind === 'event') {
      events[cls.event] = (events[cls.event] ?? 0) + 1;
      totalEvents++;
    } else if (cls.kind === 'mood') {
      moods[cls.mood] = (moods[cls.mood] ?? 0) + 1;
    } else {
      unknownTags.push(m[0]);
    }
  }
  const emphasis = [...raw.matchAll(EMPHASIS_RE)].length;
  return { events, moods, totalEvents, emphasis, unknownTags };
}

const SPACED_TAG_RE = /< ([A-Za-z][A-Za-z:_ -]{1,20})>/g;
const DISPLAY_LEAK_RE =
  /<\/?[A-Za-z][A-Za-z:_ -]{1,20}>|\[(?:laugh|laughs|laughing|chuckle|chuckles|sigh|sighs|gasp|gasps|whisper|whispers|pause|breath|breathes|cheer|cheers|giggle|giggles|exhale|exhales|tsk)\]/gi;

/**
 * Markup que se FILTRÓ al texto de display (debe ser siempre `[]`): tags con
 * forma de dirección vocal y acotaciones estilo `[laughs]`.
 */
export function findDisplayMarkupLeaks(display: string): string[] {
  const shaped = [...display.matchAll(DISPLAY_LEAK_RE)].map((m) => m[0]);
  // `< mood:playful>`: con espacio sólo cuenta si el nombre es markup de voz.
  const spaced = [...display.matchAll(SPACED_TAG_RE)]
    .filter((m) => classifySpeechTag(m[1] as string).kind !== 'unknown')
    .map((m) => m[0]);
  return [...shaped, ...spaced];
}
